import type { TFunction } from "i18next";

import { ButtonProps } from "components/ButtonNew";
import { getFileExtension } from "features/shared/lib/file";
import React from "react";
import { cn } from "utils/styling";

import type { EpcUploadHandlers, EpcUploadStates } from "./types";

import { EpcAnalyzingPhase } from "./EpcAnalyzingPhase";
import { EpcIntroPhase } from "./EpcIntroPhase";
import { EpcPreviewPhase } from "./EpcPreviewPhase";
import { EpcUploadingPhase } from "./EpcUploadingPhase";
import { EpcUploadPhase } from "./EpcUploadPhase";

export type EpcModalActionButtonConfig = Pick<
  ButtonProps,
  "className" | "disabled" | "endIconName" | "onClick" | "variant"
> & {
  label: string;
};

export type EpcModalActionRow = {
  align?: "center" | "stretch";
  buttons: EpcModalActionButtonConfig[];
  direction?: "column" | "row";
};

type EPCModalContentFactoryInput = {
  handlers: EpcUploadHandlers;
  states: EpcUploadStates;
  t: TFunction;
};

export const EPCModalContentFactory = ({
  handlers,
  states,
  t,
}: EPCModalContentFactoryInput): undefined | { actionsRows: EpcModalActionRow[]; content: React.ReactNode } => {
  const cancelButtonConfig: EpcModalActionButtonConfig = {
    className: "capitalize",
    label: t("general.cancel"),
    variant: "tertiary",
  };

  const uploadAndAnalyzeActionsProps: EpcModalActionRow[] = [
    {
      align: "center",
      buttons: [
        {
          ...cancelButtonConfig,
          onClick: handlers.handleCloseModal,
        },
      ],
    },
  ];

  const { acceptedExtensions, extractionError, file, pendingExtraction, phase, selectedFile, uploadProgress } = states;

  const hasSelectedFile = !!selectedFile;

  if (phase === "intro") {
    return {
      actionsRows: [
        {
          align: "stretch",
          buttons: [
            {
              className: "w-full",
              label: t("AddBuilding.epcModal.introUploadButton"),
              onClick: handlers.handleStartUpload,
              variant: "primary",
            },
          ],
          direction: "column",
        },
        {
          align: "stretch",
          buttons: [
            {
              label: t("AddBuilding.epcModal.introSkipButton"),
              onClick: handlers.handleSkip,
              variant: "secondary",
            },
          ],
        },
      ],
      content: <EpcIntroPhase />,
    };
  }
  if (phase === "upload") {
    return {
      actionsRows: [
        ...(hasSelectedFile
          ? [
              {
                align: "stretch" as const,
                buttons: [
                  {
                    className: "w-full",
                    label: t("AddBuilding.epcModal.uploadAndExtract"),
                    onClick: handlers.handleSubmitUpload,
                    variant: "success" as const,
                  },
                ],
                direction: "column" as const,
              },
            ]
          : []),
        {
          align: "center",
          buttons: [
            {
              ...cancelButtonConfig,
              onClick: handlers.handleBackToIntro,
            },
          ],
        },
      ],
      content: (
        <EpcUploadPhase
          acceptedExtensions={acceptedExtensions}
          extractionError={extractionError}
          onBack={handlers.handleBackToIntro}
          onFileChange={handlers.handleFileChange}
          selectedFile={selectedFile}
        />
      ),
    };
  }
  if (phase === "uploading" && file) {
    return {
      actionsRows: uploadAndAnalyzeActionsProps,
      content: <EpcUploadingPhase file={file} uploadProgress={uploadProgress} />,
    };
  }
  if (phase === "analyzing" && file) {
    return {
      actionsRows: uploadAndAnalyzeActionsProps,
      content: <EpcAnalyzingPhase file={file} />,
    };
  }
  if (phase === "preview" && pendingExtraction) {
    return {
      actionsRows: [
        {
          align: "stretch",
          buttons: [
            {
              ...cancelButtonConfig,
              className: cn(cancelButtonConfig.className, "flex-1"),
              onClick: handlers.handleBackToUpload,
              variant: "secondary",
            },
            {
              className: "flex-1",
              endIconName: "ArrowRight",
              label: t("AddBuilding.epcModal.applyButton"),
              onClick: handlers.handleApply,
              variant: "primary",
            },
          ],
          direction: "row",
        },
      ],
      content: (
        <EpcPreviewPhase
          epcData={pendingExtraction}
          fileExtension={getFileExtension(file?.name ?? pendingExtraction.uploadedFilename)}
          onBack={handlers.handleBackToUpload}
        />
      ),
    };
  }
  return undefined;
};
